import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../context/UserContext';
import { useCache } from '../context/CacheContext';
import { teacherApiService } from '../services/teacherApiService';
import './PersonalCabinet.css';
import { CacheWarning } from '../th-components/CacheWarning';

interface StaffApiResponse {
  id: number;
  patronymic: string;
  name: string;
  lastName: string;
  login: string;
  password: string;
  email: string | null;
  staffPosition: Array<{
    id: number;
    name: string;
  }>;
}

interface TeacherSubject {
  idTeacher: number;
  idSubject: number;
  subjectName: string;
  idGroups: number[];
}

interface TeacherData {
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  position: string;
  disciplines: string[];
  teacherId?: number;
  login?: string;
}

interface PasswordChangeData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface Props {
  onNavigateToDisciplines?: (disciplineName?: string) => void;
  onNavigateToGroups?: (disciplineName?: string) => void;
  onDisciplineSelect?: (disciplineName: string | undefined) => void;
}

export const PersonalCabinet: React.FC<Props> = ({ 
  onNavigateToDisciplines, 
  onNavigateToGroups,
  onDisciplineSelect
}) => {
  const { user } = useUser();
  const [teacherData, setTeacherData] = useState<TeacherData>({
    firstName: '',
    lastName: '',
    middleName: '',
    email: '',
    position: '',
    disciplines: [],
    login: ''
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { isUsingCache, showCacheWarning, setShowCacheWarning, forceCacheCheck } = useCache();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentialsMode, setCredentialsMode] = useState<'login' | 'email' | null>(null);
  const [credentialsLoading, setCredentialsLoading] = useState(false);

  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [loginData, setLoginData] = useState({
    newLogin: ''
  });

  const [emailData, setEmailData] = useState({
    newEmail: ''
  });
  
  const [passwordLoading, setPasswordLoading] = useState(false);

  const showSuccess = useCallback((message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);
  }, []);

  const fetchTeacherData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      setShowCacheWarning(false);

      if (forceRefresh) {
        setRefreshing(true);
      }

      console.log('Загрузка данных преподавателя...');
      
      if (!user?.name || !user?.lastName) {
        throw new Error('Недостаточно данных пользователя для поиска: требуется имя и фамилия');
      }

      if (forceRefresh && teacherData.teacherId) {
        teacherApiService.invalidateTeacherCache(teacherData.teacherId);
      }

      let teacher: StaffApiResponse | null = null;

      if (user.patronymic && user.patronymic.trim() !== '') {
        console.log('Поиск преподавателя с отчеством:', user.name, user.lastName, user.patronymic);
        teacher = await teacherApiService.findTeacherByName(
          user.name, 
          user.lastName, 
          user.patronymic
        );
      }

      if (!teacher) {
        console.log('Преподаватель не найден с отчеством, поиск только по имени и фамилии:', user.name, user.lastName);
        teacher = await teacherApiService.findTeacherByNameWithoutPatronymic(
          user.name, 
          user.lastName
        );
      }

      if (teacher) {
        console.log('Найден преподаватель с ID:', teacher.id);
        localStorage.setItem('teacher_id', teacher.id.toString());
        console.log('Teacher ID сохранен в localStorage:', teacher.id);

        try {
          const teacherDisciplines = await teacherApiService.getTeacherDisciplines(teacher.id);
          
          const formattedEmail = teacher.email || `${teacher.login}@novsu.ru`;
          
          const transformedData: TeacherData = {
            firstName: teacher.name,
            lastName: teacher.lastName,
            middleName: teacher.patronymic || '',
            email: formattedEmail,
            position: teacher.staffPosition[0]?.name || 'Преподаватель',
            disciplines: teacherDisciplines.length > 0 ? teacherDisciplines : ['Дисциплины не назначены'],
            teacherId: teacher.id,
            login: teacher.login
          };

          setTeacherData(transformedData);
          console.log('Набор данных преподавателя с дисциплинами:', transformedData);
          
        } catch (disciplinesError) {
          console.error('Ошибка загрузки дисциплин:', disciplinesError);
          
          const formattedEmail = teacher.email || `${teacher.login}@novsu.ru`;
          
          const transformedData: TeacherData = {
            firstName: teacher.name,
            lastName: teacher.lastName,
            middleName: teacher.patronymic || '',
            email: formattedEmail,
            position: teacher.staffPosition[0]?.name || 'Преподаватель',
            disciplines: ['Не удалось загрузить дисциплины'],
            teacherId: teacher.id,
            login: teacher.login
          };
          setTeacherData(transformedData);
        }
      } else {
        console.log('Преподаватель не найден, используются контекстные данные');
        const formattedEmail = user?.email || `${user?.login}@novsu.ru`;
        
        const fallbackData: TeacherData = {
          firstName: user?.name || '',
          lastName: user?.lastName || '',
          middleName: user?.patronymic || '',
          email: formattedEmail,
          position: 'Преподаватель',
          disciplines: ['Дисциплины не найдены'],
          login: user?.login || ''
        };
        setTeacherData(fallbackData);
        
        localStorage.removeItem('teacher_id');
      }

    } catch (err: any) {
      console.error('Ошибка при загрузке данных преподавателя:', err);
      
      const isNetworkError = 
        err.message?.includes('Failed to fetch') ||
        err.message?.includes('NetworkError') ||
        err.message?.includes('Network request failed') ||
        err.message?.includes('Превышено время ожидания') ||
        err.name === 'TypeError';
      
      if (isNetworkError) {
        forceCacheCheck();
        setShowCacheWarning(true);

        const formattedEmail = user?.email || `${user?.login}@novsu.ru`;
          
        const fallbackData: TeacherData = {
          firstName: user?.name || '',
          lastName: user?.lastName || '',
          middleName: user?.patronymic || '',
          email: formattedEmail,
          position: 'Преподаватель',
          disciplines: ['Данные загружены из кэша'],
          login: user?.login || ''
        };
        setTeacherData(fallbackData);
      } else {
        setError('Не удалось загрузить данные преподавателя');
        
        const formattedEmail = user?.email || `${user?.login}@novsu.ru`;
        
        const fallbackData: TeacherData = {
          firstName: user?.name || '',
          lastName: user?.lastName || '',
          middleName: user?.patronymic || '',
          email: formattedEmail,
          position: 'Преподаватель',
          disciplines: ['Ошибка загрузки дисциплин'],
          login: user?.login || ''
        };
        setTeacherData(fallbackData);
      }
      
      localStorage.removeItem('teacher_id');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    await fetchTeacherData(true);
  };

  useEffect(() => {
    if (user) {
      fetchTeacherData();
    }
  }, [user]);

  const handlePasswordModalOpen = useCallback(() => {
    setShowPasswordModal(true);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setError(null);
    setSuccessMessage(null);
  }, []);

  const handleCredentialsModalOpen = useCallback(() => {
    setShowCredentialsModal(true);
    setCredentialsMode(null);
    setLoginData({ newLogin: '' });
    setEmailData({ newEmail: '' });
    setError(null);
    setSuccessMessage(null);
  }, []);

  const handleCredentialsModeSelect = (mode: 'login' | 'email') => {
    setCredentialsMode(mode);
    setError(null);
  };

  const handlePasswordChange = async () => {
    try {
      setPasswordLoading(true);
      setError(null);
      setSuccessMessage(null);

      if (!passwordData.newPassword) {
        setError('Введите новый пароль');
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setError('Пароль должен содержать минимум 6 символов');
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setError('Новые пароли не совпадают');
        return;
      }

      if (!teacherData.teacherId) {
        setError('ID преподавателя не найден');
        return;
      }

      await teacherApiService.changePassword(teacherData.teacherId, {
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword
      });

      showSuccess('Пароль успешно изменен!');
      setShowPasswordModal(false);
      
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
    } catch (err) {
      console.error('Password change error:', err);
      setError(err instanceof Error ? err.message : 'Не удалось изменить пароль');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCredentialsChange = async () => {
    try {
      setCredentialsLoading(true);
      setError(null);
      setSuccessMessage(null);

      if (!teacherData.teacherId) {
        setError('ID преподавателя не найден');
        return;
      }

      if (credentialsMode === 'login') {
        if (!loginData.newLogin) {
          setError('Введите новый логин');
          return;
        }

        if (loginData.newLogin.length < 3) {
          setError('Логин должен содержать минимум 3 символа');
          return;
        }

        const loginRegex = /^[a-zA-Z0-9._-]+$/;
        if (!loginRegex.test(loginData.newLogin)) {
          setError('Логин может содержать только латинские буквы, цифры и символы ._-');
          return;
        }

        if (loginData.newLogin === teacherData.login) {
          setError('Новый логин не должен совпадать с текущим');
          return;
        }

        try {
          const availability = await teacherApiService.isLoginAvailable(loginData.newLogin);
          if (!availability.available) {
            setError(availability.message || 'Этот логин уже занят');
            return;
          }
        } catch (availabilityError) {
          console.log('Login availability check failed, proceeding...');
        }
        
        const result = await teacherApiService.changeLogin(teacherData.teacherId, {
          newLogin: loginData.newLogin,
          confirmNewLogin: loginData.newLogin 
        });

        setTeacherData(prev => ({
          ...prev,
          login: loginData.newLogin
        }));

        showSuccess('Логин успешно изменен!');
      } 
      
      else if (credentialsMode === 'email') {
        if (!emailData.newEmail) {
          setError('Введите новую почту');
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailData.newEmail)) {
          setError('Введите корректный email');
          return;
        }

        if (emailData.newEmail === teacherData.email) {
          setError('Новый email не должен совпадать с текущим');
          return;
        }

        await teacherApiService.changeEmail(teacherData.teacherId, emailData.newEmail);

        setTeacherData(prev => ({
          ...prev,
          email: emailData.newEmail
        }));

        showSuccess('Почта успешно изменена!');
      }

      setShowCredentialsModal(false);
      setCredentialsMode(null);
      setLoginData({ newLogin: '' });
      setEmailData({ newEmail: '' });
      
    } catch (err) {
      console.error('Credentials change error:', err);
      
      if (err instanceof Error) {
        if (err.message.includes('уже занят') || err.message.includes('409')) {
          setError('Этот логин уже занят. Выберите другой логин.');
        } else if (err.message.includes('400')) {
          setError('Неверный формат данных');
        } else if (err.message.includes('500')) {
          setError('Внутренняя ошибка сервера. Попробуйте позже.');
        } else if (err.message.includes('Ошибка соединения')) {
          setError('Проблемы с соединением. Проверьте интернет и попробуйте снова.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Не удалось изменить данные. Попробуйте позже.');
      }
    } finally {
      setCredentialsLoading(false);
    }
  };

  const InfoIcon = () => (
    <div className="info-icon-btn" tabIndex={0}>
      <button className="header-btn" type="button">
        <span className="info-icon-text">i</span>
        <span>Информация</span>
      </button>
      <div className="info-tooltip small">
        <div className="info-tooltip-content">
          <div className="info-header">
            <div className="info-title">
              <h3>Личный кабинет преподавателя</h3>
              <p>Здесь вы можете просмотреть свои личные данные, изменить логин, пароль или электронную почту, а также ознакомиться с перечнем преподаваемых дисциплин.</p>
            </div>
          </div>
          
          <div className="info-section">
            <h4>Основные возможности</h4>
            <div className="features-grid">
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span>Просмотр личных и учетных данных</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span>Изменение логина, пароля и email учетной записи</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span>Просмотр списка преподаваемых дисциплин</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span>Быстрый переход к управлению группами</span>
              </div>
            </div>
          </div>

          <div className="info-section">
            <h4>Как использовать</h4>
            <div className="usage-steps">
              <div className="steps">
                <span className="steps-number">1</span>
                <span>Для изменения данных нажмите кнопку "Сменить ..."</span>
              </div>
              <div className="steps">
                <span className="steps-number">2</span>
                <span>Внесите необходимые изменения в форму</span>
              </div>
              <div className="steps">
                <span className="steps-number">3</span>
                <span>Сохраните изменения или отмените редактирование</span>
              </div>
              <div className="steps">
                <span className="steps-number">4</span>
                <span>Для перехода к дисциплине нажмите на её название</span>
              </div>
            </div>
          </div>

          <div className="info-tip">
            Регулярно обновляйте пароль для обеспечения безопасности учетной записи
          </div>
        </div>
      </div>
    </div>
  );

  const RefreshButton = () => (
    <button 
      className={`header-btn pc-refresh-btn ${refreshing ? 'pc-refreshing' : ''}`}
      onClick={handleRefresh}
      disabled={refreshing}
    >
      <img 
        src="/st-icons/upload_icon.svg" 
        className={`pc-refresh-icon ${refreshing ? 'pc-refresh-spin' : ''}`}
        alt="Обновить"
      />
      <span>Обновить данные</span>
    </button>
  );

const SuccessNotification = () => {
  if (!successMessage) return null;

  return (
    <div className="lk-success-notification">
      <div className="lk-success-content">
        <div className="lk-success-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path 
              d="M16.6667 5L7.5 14.1667L3.33333 10" 
              stroke="white" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="lk-success-text">
          <div className="lk-success-title">Успешно!</div>
          <div className="lk-success-message">{successMessage}</div>
        </div>
        <button 
          className="lk-success-close"
          onClick={() => setSuccessMessage(null)}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path 
              d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

  const handlePasswordDataChange = useCallback((field: keyof PasswordChangeData, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleLoginFieldChange = useCallback((value: string) => {
    setLoginData({ newLogin: value });
  }, []);

  const handleEmailFieldChange = useCallback((value: string) => {
    setEmailData({ newEmail: value });
  }, []);

  const handleDisciplineClick = (discipline: string) => {
    const isValidDiscipline = 
      discipline !== 'Дисциплины не назначены' && 
      discipline !== 'Не удалось загрузить дисциплины' && 
      discipline !== 'Ошибка загрузки дисциплин';

    if (isValidDiscipline) {
      if (onDisciplineSelect) {
        onDisciplineSelect(discipline);
      }
      
      if (onNavigateToGroups) {
        onNavigateToGroups(discipline);
      } else if (onNavigateToDisciplines) {
        onNavigateToDisciplines(discipline);
      }
    }
  };

  if (!user) {
    return (
      <div className="personal-cabinet">
        <SuccessNotification />
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          Загрузка данных пользователя...
        </div>
      </div>
    );
  }

  const isCredentialsSubmitDisabled = credentialsLoading || 
    (credentialsMode === 'login' && !loginData.newLogin) ||
    (credentialsMode === 'email' && !emailData.newEmail);

  return (
    <div className="personal-cabinet">
      <SuccessNotification />

      <div className="cabinet-header">
        <InfoIcon />
        <RefreshButton />
      </div>

      {showCacheWarning && <CacheWarning />}

      <div className="personal-info-main">
        <div className="personal-info-section">
          <div className="info-column">
            <div className="info-item">
              <span className="info-label">Фамилия:</span>
              <span className="info-value">{teacherData.lastName || 'Не указано'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Имя:</span>
              <span className="info-value">{teacherData.firstName || 'Не указано'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Отчество:</span>
              <span className="info-value">{teacherData.middleName || 'Не указано'}</span>
            </div>
          </div>
        </div>
        
        <div className="personal-info-section">
          <div className="info-column">
            <div className="info-item">
              <span className="info-label">Эл. почта:</span>
              <span className="info-value">{teacherData.email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Логин:</span>
              <span className="info-value">{teacherData.login}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="buttons-row">
        <button 
          className="section-button password-btn"
          onClick={handlePasswordModalOpen}
          disabled={loading}
        >
          Сменить пароль
        </button>
        <button 
          className="section-button login-btn"
          onClick={handleCredentialsModalOpen}
          disabled={loading}
        >
          Сменить логин или почту
        </button>
      </div>

      <div className="disciplines-section">
        <div className="disciplines-header">
          <label className="disciplines-label">Дисциплины:</label>
        </div>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
            Загрузка дисциплин...
          </div>
        ) : (
          <div className="disciplines-list">
            {teacherData.disciplines.length > 0 ? (
              teacherData.disciplines.map((discipline, index) => (
                <div 
                  key={index} 
                  className={`discipline-item ${discipline !== 'Дисциплины не назначены' && discipline !== 'Не удалось загрузить дисциплины' && discipline !== 'Ошибка загрузки дисциплин' ? 'clickable' : ''}`}
                  onClick={() => handleDisciplineClick(discipline)}
                >
                  {discipline}
                  {discipline !== 'Дисциплины не назначены' && 
                   discipline !== 'Не удалось загрузить дисциплины' && 
                   discipline !== 'Ошибка загрузки дисциплин' && (
                    <span className="discipline-arrow">→</span>
                  )}
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                Нет данных о дисциплинах
              </div>
            )}
          </div>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
          Загрузка данных...
        </div>
      )}
      
      {error && (
        <div className="pc-error-message">
          {error}
        </div>
      )}

      {/* Модальное окно смены пароля */}
      {showPasswordModal && (
        <div className="pc-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="lk-pc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pc-modal-header">
              <h3>Смена пароля</h3>
              <button 
                className="pc-modal-close"
                onClick={() => setShowPasswordModal(false)}
              >
                ×
              </button>
            </div>

            <div className="pc-modal-content">
              <div className="pc-form-group">
                <label>Новый пароль</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordDataChange('newPassword', e.target.value)}
                  className="pc-input"
                  placeholder="Введите новый пароль (минимум 6 символов)"
                />
              </div>
              <div className="pc-form-group">
                <label>Подтвердите новый пароль</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordDataChange('confirmPassword', e.target.value)}
                  className="pc-input"
                  placeholder="Повторите новый пароль"
                />
              </div>
              <div className="pc-modal-actions">
                <button
                  className="pc-confirm-btn"
                  onClick={handlePasswordChange}
                  disabled={passwordLoading || !passwordData.newPassword || !passwordData.confirmPassword}
                >
                  {passwordLoading ? 'Смена пароля...' : 'Сменить пароль'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    {/* Модальное окно смены логина или почты */}
    {showCredentialsModal && (
      <div className="pc-modal-overlay" onClick={() => setShowCredentialsModal(false)}>
        <div className="lk-pc-modal" onClick={(e) => e.stopPropagation()}>
          <div className="pc-modal-header">
            <h3>
              {!credentialsMode && 'Смена данных учетной записи'}
              {credentialsMode === 'login' && 'Смена логина'}
              {credentialsMode === 'email' && 'Смена почты'}
            </h3>
            <button 
              className="pc-modal-close"
              onClick={() => setShowCredentialsModal(false)}
            >
              ×
            </button>
          </div>

          <div className="pc-modal-content">
            {!credentialsMode ? (
              <div className="credentials-mode-selector">
                <button 
                  className="credentials-mode-btn"
                  onClick={() => handleCredentialsModeSelect('login')}
                >
                  <div className="credentials-mode-icon">@</div>
                  <div className="credentials-mode-info">
                    <h4>Сменить логин</h4>
                    <p>Изменить логин для входа в систему</p>
                  </div>
                </button>
                <button 
                  className="credentials-mode-btn"
                  onClick={() => handleCredentialsModeSelect('email')}
                >
                  <div className="credentials-mode-icon">✉</div>
                  <div className="credentials-mode-info">
                    <h4>Сменить почту</h4>
                    <p>Изменить электронную почту аккаунта</p>
                  </div>
                </button>
              </div>
            ) : (
              <>
                {error && (
                  <div className="pc-error-message" style={{ marginTop: 0 }}>
                    {error}
                  </div>
                )}

                {credentialsMode === 'login' ? (
                  <div className="pc-form-group">
                    <label>Новый логин</label>
                    <input
                      type="text"
                      value={loginData.newLogin}
                      onChange={(e) => handleLoginFieldChange(e.target.value)}
                      className="pc-input"
                      placeholder="Введите новый логин"
                    />
                    <small className="pc-form-hint">Только латинские буквы, цифры и символы ._- (минимум 3 символа)</small>
                  </div>
                ) : (
                  <div className="pc-form-group">
                    <label>Новая почта</label>
                    <input
                      type="email"
                      value={emailData.newEmail}
                      onChange={(e) => handleEmailFieldChange(e.target.value)}
                      className="pc-input"
                      placeholder="Введите новый email"
                    />
                  </div>
                )}

                <div className="pc-modal-actions">
                  <button 
                    className="pc-secondary-btn"
                    onClick={() => setCredentialsMode(null)}
                  >
                    Назад к выбору
                  </button>
                  <button
                    className="pc-confirm-btn"
                    onClick={handleCredentialsChange}
                    disabled={isCredentialsSubmitDisabled}
                  >
                    {credentialsLoading ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    </div>
  );
};